import { IContestsStorePayload } from '../../../../services/Contests/interfaces'

/** Accepts a date or a full datetime and returns the `YYYY-MM-DD` a date input needs. */
export const normalizeDate = (d: string | undefined | null) => {
  if (!d) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d
  if (d.length >= 10) return d.slice(0, 10)
  try {
    return new Date(d).toISOString().split('T')[0]
  } catch {
    return ''
  }
}

export interface IDateFieldBounds {
  /** `undefined`, never `''` — an empty `min`/`max` attribute makes Chrome reject any typed date. */
  min?: string
  max?: string
}

export interface IContestDateBounds {
  today: string
  tomorrow: string
  rd: string
  sd: string
  dd: string
  od: string
  wds: string
  wde: string
  /** Soft (orange) hints about the chronological order; hard errors take precedence. */
  warnings: Partial<Record<string, string>>
  /** Native min/max per field, already reconciled so a stale stored date never outranks the "tomorrow" floor. */
  bounds: Record<
    | 'registration_deadline'
    | 'submission_deadline'
    | 'delivery_deadline'
    | 'opening_event_date'
    | 'withdraw_start_date'
    | 'withdraw_end_date',
    IDateFieldBounds
  >
}

/** Normalized values plus the ordering warnings shown under each of the six dates. */
export function buildContestDateBounds(form: IContestsStorePayload, allowLegacyDates: boolean): IContestDateBounds {
  const today = new Date().toISOString().split('T')[0]
  const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  const rd = normalizeDate(form.registration_deadline)
  const sd = normalizeDate(form.submission_deadline)
  const dd = normalizeDate(form.delivery_deadline)
  const od = normalizeDate(form.opening_event_date)
  const wds = normalizeDate(form.withdraw_start_date)
  const wde = normalizeDate(form.withdraw_end_date)

  const warnings: Partial<Record<string, string>> = {}
  if (rd) {
    if (rd <= today) warnings.registration_deadline = 'Deve ser posterior a hoje'
    else if (sd && rd >= sd) warnings.registration_deadline = 'Deve ser anterior à data de envio dos dados'
  }
  if (sd) {
    if (rd && sd <= rd) warnings.submission_deadline = 'Deve ser posterior à data de inscrição'
    else if (dd && sd >= dd) warnings.submission_deadline = 'Deve ser anterior à data de entrega das obras'
  }
  if (dd) {
    if (sd && dd <= sd) warnings.delivery_deadline = 'Deve ser posterior à data de envio dos dados'
    else if (od && dd >= od) warnings.delivery_deadline = 'Deve ser anterior à data de abertura do concurso'
  }
  if (od) {
    if (dd && od <= dd) warnings.opening_event_date = 'Deve ser posterior à data de entrega das obras'
    else if (wds && od >= wds) warnings.opening_event_date = 'Deve ser anterior à data inicial de retirada das obras'
  }
  if (wds) {
    if (od && wds <= od) warnings.withdraw_start_date = 'Deve ser posterior à data de abertura do concurso'
    else if (wde && wds > wde) warnings.withdraw_start_date = 'Deve ser igual ou anterior à data final de retirada'
  }
  if (wde && wds && wde < wds) {
    warnings.withdraw_end_date = 'Deve ser igual ou posterior à data inicial de retirada'
  }

  // A legacy date (editing or duplicating an existing contest) can already sit before "tomorrow", before a
  // sibling's stale ceiling, or — since old records were never cross-validated on every field — after a
  // sibling's stale floor. A blank creation never has any of these problems, so the bounds only relax once
  // we know we're looking at data that came from the server, and each bound is dropped independently based
  // on whether the field's own stored value already violates it, before checking if what's left still
  // crosses (the same reconciliation `warnings` already does, just enforced on the native min/max too).
  // `max: max || undefined` for the same reason `FilterDateRange` does it: an empty string is still a
  // real (invalid) attribute value, and Chrome reads it as "reject any typed date" rather than "no limit".
  const clampRange = (min: string, max: string, value: string): IDateFieldBounds => {
    if (!allowLegacyDates) return { min, max: max || undefined }
    const safeMin = value && value < min ? undefined : min
    const safeMax = value && max && value > max ? undefined : max || undefined
    if (safeMin && safeMax && safeMin > safeMax) return { max: safeMax }
    return { min: safeMin, max: safeMax }
  }

  const bounds = {
    registration_deadline: clampRange(tomorrow, sd || dd || od || wds || wde || '', rd),
    submission_deadline: clampRange(rd || tomorrow, dd || od || wds || wde || '', sd),
    delivery_deadline: clampRange(sd || rd || tomorrow, od || wds || wde || '', dd),
    opening_event_date: clampRange(dd || sd || rd || tomorrow, wds || wde || '', od),
    withdraw_start_date: clampRange(od || dd || sd || rd || tomorrow, wde || '', wds),
    withdraw_end_date: clampRange(wds || od || dd || sd || rd || tomorrow, '', wde)
  }

  return { today, tomorrow, rd, sd, dd, od, wds, wde, warnings, bounds }
}
